import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function QualityAssurancePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const standards = [
    {
      icon: 'ri-shield-check-line',
      title: 'FSSAI Certified',
      description: 'Food Safety and Standards Authority of India compliance ensuring safe and hygienic products'
    },
    {
      icon: 'ri-award-line',
      title: 'ISO 22000',
      description: 'International food safety management system certification for quality assurance'
    },
    {
      icon: 'ri-plant-line',
      title: 'Organic Certification',
      description: 'USDA and EU organic standards for naturally grown agricultural products'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Phytosanitary',
      description: 'Plant health certificates meeting international phytosanitary standards'
    },
    {
      icon: 'ri-test-tube-line',
      title: 'Lab Testing',
      description: 'Third-party laboratory testing for pesticides, moisture, and quality parameters'
    },
    {
      icon: 'ri-microscope-line',
      title: 'HACCP Certified',
      description: 'Hazard Analysis Critical Control Point system for food safety management'
    }
  ];

  const checks = [
    {
      stage: 'Pre-Purchase Inspection',
      items: [
        'Supplier verification and audit',
        'Farm and facility inspection',
        'Quality standard assessment',
        'Documentation review'
      ]
    },
    {
      stage: 'Product Testing',
      items: [
        'Moisture content analysis',
        'Pesticide residue testing',
        'Purity and grade verification',
        'Microbiological testing'
      ]
    },
    {
      stage: 'Processing Quality',
      items: [
        'Cleaning and sorting standards',
        'Processing hygiene checks',
        'Packaging quality control',
        'Weight and measurement accuracy'
      ]
    },
    {
      stage: 'Pre-Shipment Final Inspection',
      items: [
        'Final product quality check',
        'Packaging integrity verification',
        'Documentation completeness',
        'Container loading supervision'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/b857441a8282eff6e67d45b8b6cd5de0.jpeg" alt="Horizon Pathways Logo" className="w-12 h-12 object-contain" />
              <span className="text-xl font-semibold text-gray-900">Horizon Pathways</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Home</Link>
              <Link to="/products" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Products</Link>
              <a href="#services" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Services</a>
              <a href="#about" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">About</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Contact</a>
            </div>
            <button className="px-6 py-2 border-2 border-emerald-600 text-emerald-600 rounded-full text-sm font-medium transition-all hover:bg-emerald-600 hover:text-white whitespace-nowrap">Get Quote</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-8 relative overflow-hidden" style={{ backgroundColor: '#F8F6F3' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
            <i className="ri-shield-check-line text-emerald-700"></i>
            <span className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Quality Assurance Services</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
            Premium Quality <br />
            <span className="italic font-serif text-emerald-700">Guaranteed Every Time</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10">
            We maintain the highest quality standards through rigorous testing and inspection at every stage. Our comprehensive quality assurance process ensures only premium products reach international markets.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button className="px-8 py-4 bg-emerald-600 text-white rounded-full text-base font-semibold hover:bg-emerald-700 transition-all whitespace-nowrap">
              Request Quality Report
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button className="px-8 py-4 border-2 border-gray-900 text-gray-900 rounded-full text-base font-semibold hover:bg-gray-900 hover:text-white transition-all whitespace-nowrap">
              View Certifications
              <i className="ri-file-line ml-2"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Standards Grid */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">CERTIFICATIONS &amp; STANDARDS</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">International Quality Standards</h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">Our products meet and exceed global quality and safety standards</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {standards.map((standard, index) => (
              <div key={index} className="p-8 rounded-2xl bg-white border-2 border-gray-100 hover:border-emerald-600 hover:shadow-xl transition-all duration-300 group">
                <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-all">
                  <i className={`${standard.icon} text-3xl text-emerald-600 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{standard.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{standard.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Checks */}
      <section className="py-20 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">INSPECTION PROCESS</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Multi-Stage Quality Checks</h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">Comprehensive inspection at every stage of the export process</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {checks.map((check, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl font-bold text-emerald-600">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{check.stage}</h3>
                </div>
                <ul className="space-y-3">
                  {check.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-emerald-600 text-lg mt-0.5"></i>
                      <span className="text-sm text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testing Labs Section */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://public.readdy.ai/ai/img_res/417078c07b10a7ede44b2b04040ffabc.jpg" alt="Quality Testing Laboratory" className="w-full h-[500px] object-cover object-top rounded-3xl shadow-2xl" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">TESTING FACILITIES</p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">State-of-the-Art Testing Labs</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">We partner with accredited laboratories equipped with advanced testing equipment to ensure accurate and reliable quality analysis of all agricultural products.</p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-test-tube-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Chemical Analysis</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Testing for pesticide residues, heavy metals, aflatoxins, and chemical contaminants</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-microscope-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Microbiological Testing</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Detection of bacteria, mold, yeast, and pathogenic microorganisms</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-water-percent-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Physical Parameters</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Moisture content, purity percentage, broken grain analysis, and foreign matter detection</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-flask-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Nutritional Analysis</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">Protein content, fat content, fiber, and nutritional value assessment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-8" style={{ backgroundColor: '#E67E50' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Experience Premium Quality Standards</h2>
          <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">Partner with us for guaranteed quality products that meet international standards. Request a quality inspection report today.</p>
          <button className="px-10 py-4 bg-white text-gray-900 rounded-full text-base font-semibold hover:bg-gray-100 transition-all whitespace-nowrap">
            Request Quality Report
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
